import styles from '../GenerationWorkspace.module.css';
import { brandConfig } from '../../../config/brands';
import type { AttributeTreeNode, GenerationSettings } from '../state/types';
import type { PromptModel } from '../hooks/usePromptModels';
import type { RecommendedKeys } from '../../common/HierarchicalSelect';
import { Tooltip } from '../../common/Tooltip';
import { HelpTooltip } from '../../common/HelpTooltip';
import { AttributeNodeEditor } from './AttributeNodeEditor';

/**
 * 人物セクション。テンプレート適用中は閉じて操作不可にし、注記をアコーディオンに重ねる。
 * GenerationWorkspace の JSX からの切り出しで、DOM 構造・クラス名は不変。
 */
export function PersonSection({
  order,
  orderedAttributeTree,
  settings,
  setSettings,
  onAttributeChange,
  recommendedKeysByGroup,
  personSummary,
  modelsQuery,
  selectedModelId,
  setSelectedModelId,
  onModelToggle,
  isTemplateActive,
  templateLockClass,
  renderOpen,
  onToggle,
  onClose,
}: {
  order: number;
  orderedAttributeTree: AttributeTreeNode[];
  settings: GenerationSettings;
  setSettings: (next: GenerationSettings | ((prev: GenerationSettings) => GenerationSettings)) => void;
  onAttributeChange: (key: string, value: unknown) => void;
  recommendedKeysByGroup: Record<string, RecommendedKeys> | undefined;
  personSummary: string;
  modelsQuery: { data?: PromptModel[]; isLoading: boolean; isError: boolean };
  selectedModelId: string | undefined;
  setSelectedModelId: (id: string | undefined) => void;
  onModelToggle: (model: PromptModel) => void;
  isTemplateActive: boolean;
  templateLockClass: string;
  /** テンプレート適用中は実 state を保持したまま閉じて見せる (personRenderOpen) */
  renderOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const excludePeople = (settings.attributes?.person as Record<string, unknown> | undefined)?.none === true;
  return (
    <div className={styles.accordionLockWrap} style={{ order }}>
      {isTemplateActive && (
        <div className={styles.templateDisabledOverlay}>テンプレート適用中は利用できません</div>
      )}
    <div
      className={`accordion-section ${renderOpen ? 'accordion-section-open' : ''}${templateLockClass}`}
    >
      <button
        type="button"
        className="accordion-header"
        onClick={onToggle}
        aria-expanded={renderOpen}
      >
        <span className="accordion-title">
          <img src="/single_man_focus.svg" alt="" className="accordion-title-icon" />
          <span>人物</span>
          <span className="accordion-summary">{personSummary}</span>
        </span>
        <span className={`accordion-icon ${renderOpen ? 'accordion-icon-open' : ''}`}>
          {renderOpen ? '▼' : '▶'}
        </span>
      </button>
      <div className={`accordion-panel ${renderOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
        {/* 「人物・キャラクターを含めない」ON のときは人物属性の選択を無効化してグレーアウトする
            (動画のキャスティングと同じ見せ方。選択値は保持し、OFF に戻せば元どおり) */}
        <div
          className={styles.attributeGrid}
          style={excludePeople ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
          aria-disabled={excludePeople}
        >
          {orderedAttributeTree
            .filter((node) => node.key === 'person')
            .map((node) => {
              // 俳優カルーセルは「人物・キャラクターを含めない」ON でも隠さない (パネルの高さが
              // 動かないように。グリッドごとグレーアウトし、選択中の俳優は GenerationWorkspace が解除する)
              const modelsSection = (
                      <div className={styles.modelsInlineSection}>
                        <div className={styles.sectionHeaderInline}>
                          <span className={styles.sectionTitleSub} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {brandConfig.labels.aiActor}選択
                            <HelpTooltip ariaLabel={`${brandConfig.labels.aiActor}選択のヘルプ`}>
                              {brandConfig.labels.aiActor}を選択しない場合、指定した人物属性で新しい人物がランダムに生成されます。
                            </HelpTooltip>
                          </span>
                        </div>
                        {modelsQuery.isLoading ? (
                          <div className={styles.loading}>{brandConfig.labels.aiActor}を取得中…</div>
                        ) : modelsQuery.isError ? (
                          <div className={styles.error}>{brandConfig.labels.aiActor}の取得に失敗しました</div>
                        ) : modelsQuery.data ? (
                          <div className={styles.modelCarousel}>
                            <div className={styles.modelScrollContainer}>
                              <ul className={styles.modelGrid}>
                                {/* 「おまかせ」プレースホルダー（一番左） */}
                                <li>
                                  <button
                                    type="button"
                                    className={`${styles.modelCard} ${styles.modelCardNone} ${!selectedModelId ? styles.modelCardSelected : ''}`}
                                    onClick={() => {
                                      setSelectedModelId(undefined);
                                      setSettings((prev) => ({
                                        ...prev,
                                        model: undefined,
                                        modelMeta: null,
                                      }));
                                    }}
                                    aria-pressed={!selectedModelId}
                                  >
                                    <div className={styles.modelImageNone}>
                                      <img src="/anonyface.png" alt="" className={styles.modelNoneIcon} style={{ opacity: 0.5 }} />
                                    </div>
                                    <div className={styles.modelMeta}>
                                      おまかせ
                                    </div>
                                  </button>
                                </li>
                                {modelsQuery.data.map((model) => {
                                  const isSelected = model.id === selectedModelId;
                                  return (
                                    <li
                                      key={model.id}
                                      ref={isSelected ? (el) => {
                                        if (el) {
                                          const container = el.closest(`.${styles.modelScrollContainer}`) as HTMLElement | null;
                                          if (container) {
                                            const targetLeft = el.offsetLeft - container.clientWidth / 2 + el.offsetWidth / 2;
                                            container.scrollTo({ left: targetLeft, behavior: 'smooth' });
                                          }
                                        }
                                      } : undefined}
                                    >
                                      <button
                                        type="button"
                                        className={`${styles.modelCard} ${isSelected ? styles.modelCardSelected : ''}`}
                                        onClick={() => onModelToggle(model)}
                                        aria-pressed={isSelected}
                                      >
                                        {model.is_exclusive && (
                                          <span className={styles.modelExclusiveBadge}>専属</span>
                                        )}
                                        <div className={styles.modelImage}>
                                          {model.face_image_public_url ? (
                                            <img src={model.face_image_public_url} alt={model.display_name} />
                                          ) : model.image_url ? (
                                            <img src={model.image_url} alt={model.display_name} />
                                          ) : null}
                                        </div>
                                        <div className={styles.modelMeta}>
                                          {model.display_name}
                                        </div>
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.selectionList}>
                            <span className={styles.empty}>利用可能な俳優がありません。</span>
                          </div>
                        )}
                      </div>
              );

              return (
                <AttributeNodeEditor
                  key={node.id}
                  node={node}
                  value={settings.attributes?.[node.key]}
                  onChange={onAttributeChange}
                  modelsSection={modelsSection}
                  recommendedKeysByGroup={recommendedKeysByGroup}
                />
              );
            })}
        </div>
        <div className={styles.accordionPanelFooter} style={{ justifyContent: 'space-between' }}>
          {/* 人物を含めないモード (動画のキャスティングと同じ文言)。値は既存の attributes.person.none
              (プロンプト生成側の none_person / person_included と同期。backend/services/image_generation/prompt_builder.py
              は ON のとき人物属性を出さず「人物を一切登場させない」指示を出す)。ON にすると GenerationWorkspace の
              effect (excludePeople) が選択中の AI 俳優を解除する。生成ボタンで効く条件なので
              トグルではなくチェックボックス (.claude/rules/toggle-vs-checkbox.md) */}
          <label className="form-checkbox-label" style={{ marginLeft: 4 }}>
            <input
              type="checkbox"
              checked={excludePeople}
              onChange={(e) => {
                const checked = e.target.checked;
                setSettings((prev) => {
                  const prevPerson = (prev.attributes?.person as Record<string, unknown> | undefined) ?? {};
                  return {
                    ...prev,
                    attributes: { ...prev.attributes, person: { ...prevPerson, none: checked } },
                  };
                });
              }}
            />
            <span>人物・キャラクターを含めない</span>
          </label>
          <Tooltip content="閉じる">
            <button
              type="button"
              className={styles.iconButton}
              onClick={onClose}
            >
              <img src="/move_collapse_vertical.svg" alt="閉じる" style={{ width: '16px', height: '16px' }} />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
    </div>
  );
}
